import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import Button from '../_Core/Button';

interface IProjectReviewData {
  FormType: 'MediaRelease' | 'HoldHarmless' | string;
  FairName: string;
  StudentFullName: string;
  BodyHtml: string;
  Header: string;
  Label: string;
  Question: string;
  PermissionLabel: string;
  Signed: boolean;
  Approved?: boolean | null;
  SignatureDisplay?: string | null;
  SignatureRequest: string;
  EsignSignedIn: boolean;
  SignatureTemplate?: string | null;
  EsignEmail?: string | null;
}

interface IEsignUser {
  SignedIn: boolean;
  FirstName?: string;
  LastName?: string;
  Email?: string;
  SignatureTemplate?: string;
}

const ProjectReviewPage = () => {
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const signatureRequest = query.get('SignatureRequest') || '';
  const mediaRelease = query.get('MediaRelease') === 'Y' || query.get('MediaRelease') === '1' || query.get('MediaRelease')?.toLowerCase() === 'true';
  const holdHarmless = query.get('HoldHarmless') === 'Y' || query.get('HoldHarmless') === '1' || query.get('HoldHarmless')?.toLowerCase() === 'true';

  const [data, setData] = useState<IProjectReviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [esign, setEsign] = useState<IEsignUser | null>(null);
  const [approved, setApproved] = useState('true');
  const [signature, setSignature] = useState('');
  const [saving, setSaving] = useState(false);

  // login / register form state
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const load = () => {
    if (!signatureRequest) {
      setError('Missing signature request token.');
      setLoading(false);
      return;
    }

    setLoading(true);
    const params = new URLSearchParams();
    params.set('signatureRequest', signatureRequest);
    if (mediaRelease) params.set('mediaRelease', 'true');
    if (holdHarmless) params.set('holdHarmless', 'true');

    store.server
      .postApi<ServerResponse<IProjectReviewData>>(`../ProjectReview/Data?${params.toString()}`, {})
      .then((x) => {
        if (x.Success) {
          setData(x.Value);
          setError(null);
          if (x.Value.Approved != null) setApproved(x.Value.Approved ? 'true' : 'false');
          if (x.Value.Signed && x.Value.SignatureDisplay) setSignature(x.Value.SignatureDisplay);
          setEsign({
            SignedIn: x.Value.EsignSignedIn,
            SignatureTemplate: x.Value.SignatureTemplate || undefined,
            Email: x.Value.EsignEmail || undefined,
          });
        } else {
          setError(x.Message || 'Unable to load signature request.');
        }
      })
      .catch((err) => {
        console.error(err);
        setError('Unable to load signature request.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureRequest]);

  const esignLogin = () => {
    store.server
      .postApi<ServerResponse<IEsignUser>>('../ProjectReview/EsignLogin', { email, password })
      .then((x) => {
        if (x.Success) {
          setEsign(x.Value);
          toast.success('Signed in');
          load();
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      });
  };

  const esignRegister = () => {
    store.server
      .postApi<ServerResponse<IEsignUser>>('../ProjectReview/EsignRegister', {
        firstName,
        lastName,
        email,
        password,
      })
      .then((x) => {
        if (x.Success) {
          setEsign(x.Value);
          toast.success('Account created');
          load();
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      });
  };

  const esignForgot = () => {
    store.server.postApi<ServerResponse<string>>('../ProjectReview/EsignForgotPassword', { email }).then((x) => {
      if (x.Success) {
        toast.success(typeof x.Value === 'string' ? x.Value : x.Message || 'Reset email sent.');
        setMode('login');
      } else {
        toast.error(x.Message, { autoClose: false });
      }
    });
  };

  const esignLogout = () => {
    store.server.postApi<ServerResponse<IEsignUser>>('../ProjectReview/EsignLogout', {}).then(() => {
      setEsign({ SignedIn: false });
      load();
    });
  };

  const sign = () => {
    if (!data) return;
    if (!esign?.SignedIn) {
      toast.warning('Please sign in first.');
      return;
    }
    if (!signature.trim()) {
      toast.warning('Signature is required.');
      return;
    }
    if (data.SignatureTemplate && signature.trim().toLowerCase() !== data.SignatureTemplate.trim().toLowerCase()) {
      toast.error('Signature does not match. Type your name exactly as shown.', { autoClose: false });
      return;
    }

    setSaving(true);
    store.server
      .postApi<ServerResponse<IProjectReviewData>>('../ProjectReview/Sign', {
        SignatureRequest: data.SignatureRequest || signatureRequest,
        Signature: signature.trim(),
        Approved: approved === 'true',
        MediaRelease: data.FormType === 'MediaRelease',
        HoldHarmless: data.FormType === 'HoldHarmless',
      })
      .then((x) => {
        if (x.Success) {
          setData(x.Value);
          toast.success('Signed successfully.');
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div className="main-body-page grow" data-testid="project-review-page">
        <h2>Project Review</h2>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-body-page grow" data-testid="project-review-page">
        <div className="alert alert-danger" data-testid="project-review-error">
          <h3>
            <i className="fa fa-exclamation-triangle" /> Expired Request
          </h3>
          <hr />
          {error}
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="main-body-page grow" data-testid="project-review-page">
      <h2
        data-testid="project-review-header"
        dangerouslySetInnerHTML={{
          __html: data.Header || (data.FormType === 'HoldHarmless' ? 'Hold Harmless' : 'Media Release'),
        }}
      />
      <h3>
        {data.Label ? <span dangerouslySetInnerHTML={{ __html: data.Label }} /> : null} {data.StudentFullName}
      </h3>
      <div dangerouslySetInnerHTML={{ __html: data.BodyHtml || '' }} />
      {data.Question ? (
        <div className="head-room" data-testid="project-review-question" dangerouslySetInnerHTML={{ __html: data.Question }} />
      ) : null}

          {!data.EsignSignedIn && (
            <div className="soft-border head-room" data-testid="esign-login-panel">
              <h4>E-Sign Sign In</h4>
              {mode === 'login' && (
                <div className="form-horizontal">
                  <div className="form-group">
                    <label className="col-sm-3 control-label">Email</label>
                    <div className="col-sm-6">
                      <input
                        className="form-control"
                        data-testid="esign-email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="col-sm-3 control-label">Password</label>
                    <div className="col-sm-6">
                      <input
                        type="password"
                        className="form-control"
                        data-testid="esign-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="col-sm-6 col-sm-offset-3">
                      <Button type="login" text="Sign In" onClick={esignLogin} />
                      <button type="button" className="btn btn-link" onClick={() => setMode('register')}>
                        Create account
                      </button>
                      <button type="button" className="btn btn-link" onClick={() => setMode('forgot')}>
                        Forgot password
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {mode === 'register' && (
                <div className="form-horizontal">
                  <div className="form-group">
                    <label className="col-sm-3 control-label">First Name</label>
                    <div className="col-sm-6">
                      <input className="form-control" data-testid="esign-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="col-sm-3 control-label">Last Name</label>
                    <div className="col-sm-6">
                      <input className="form-control" data-testid="esign-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="col-sm-3 control-label">Email</label>
                    <div className="col-sm-6">
                      <input className="form-control" data-testid="esign-email" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="col-sm-3 control-label">Password</label>
                    <div className="col-sm-6">
                      <input type="password" className="form-control" data-testid="esign-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="col-sm-6 col-sm-offset-3">
                      <Button type="register" text="Create Account" onClick={esignRegister} />
                      <button type="button" className="btn btn-link" onClick={() => setMode('login')}>
                        Back to sign in
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {mode === 'forgot' && (
                <div className="form-horizontal">
                  <div className="form-group">
                    <label className="col-sm-3 control-label">Email</label>
                    <div className="col-sm-6">
                      <input className="form-control" data-testid="esign-email" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="col-sm-6 col-sm-offset-3">
                      <Button type="send" text="Send Reset Link" onClick={esignForgot} />
                      <button type="button" className="btn btn-link" onClick={() => setMode('login')}>
                        Back to sign in
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {data.EsignSignedIn && (
            <div className="head-room" data-testid="esign-signed-in">
              <div className="flex-between">
                <div>
                  Signed in as <strong>{data.EsignEmail || esign?.Email}</strong>
                </div>
                <Button type="logout" text="Sign out" onClick={esignLogout} />
              </div>
            </div>
          )}

          <div className="form-horizontal head-room">
            {data.FormType === 'MediaRelease' && (
              <div className="form-group">
                <label className="col-sm-4 control-label">
                  {data.PermissionLabel ? (
                    <span dangerouslySetInnerHTML={{ __html: data.PermissionLabel }} />
                  ) : (
                    'Permission'
                  )}
                </label>
                <div className="col-sm-5">
                  <select
                    className="form-control"
                    data-testid="media-release-permission"
                    disabled={data.Signed}
                    value={approved}
                    onChange={(e) => setApproved(e.target.value)}
                  >
                    <option value="true">YES, I give my permission</option>
                    <option value="false">NO, I do not give permission</option>
                  </select>
                </div>
              </div>
            )}
            <div className="form-group">
              <label className="col-sm-4 control-label">Signature:</label>
              <div className="col-sm-5">
                {!data.Signed && data.SignatureTemplate && (
                  <div>
                    Type your signature as follows: <strong data-testid="signature-template">{data.SignatureTemplate}</strong>
                  </div>
                )}
                <input
                  className="form-control"
                  data-testid="signature-input"
                  value={signature}
                  readOnly={data.Signed}
                  onChange={(e) => setSignature(e.target.value)}
                />
              </div>
            </div>
            {!data.Signed && (
              <div className="form-group">
                <div className="col-sm-5 col-sm-offset-4">
                  <button
                    type="button"
                    className="btn btn-success"
                    data-testid="sign-button"
                    disabled={!data.EsignSignedIn || saving}
                    onClick={sign}
                  >
                    {saving ? 'Signing...' : 'Sign'}
                  </button>
                </div>
              </div>
            )}
            {data.Signed && (
              <div className="alert alert-success" data-testid="already-signed">
                Already signed.
              </div>
            )}
          </div>
    </div>
  );
};

export default ProjectReviewPage;
